// Common
import {getStore} from '../store';
// Services
import {Graphic} from './Graphic';
import {Graphics} from './GraphicService';
import {MapService} from '../MapService';
import {TokenService} from '../TokenService';
import {getJsonByURL} from '../CloudStorageService';
import {SupportedToken} from '../TokenService';
// Classes
import {LegendGroup} from '@/lib/classes/Legend/LegendGroup';
// WMTS
import {createWMTSTileGraphic, WMTSTileGraphic} from './WMTSTileGraphic';
// PARCEL
import {REPGraphic} from './REPGraphic';
import {createParcelContent} from '@/lib/classes/InfoWindowContent';
// TAX_PARCEL
import {TaxParcelGraphic} from './TaxParcelGraphic';
// ESRI_VECTOR
import {VectorTileGraphic} from './VectorTileGraphic/VectorTileGraphic';
import {createLegendGroups} from './VectorTileGraphic/createLegendGroups';
import type {StyleSpecification} from '@maplibre/maplibre-gl-style-spec';
import {LegendItem} from '@/lib/classes/Legend/LegendItem';
import {toActiveFilter} from '@/lib/filters/layers/layerFilters';
import type {ActiveFiltersByLayer, ActiveLayerFilter, LayerFilter, LayerFilterValue} from '@/lib/filters/layers/layerFilters';
import {wrapError} from '@/lib/errors';

export enum LayerType {
  DRONE = 'droneImagery',
  ESRI_VECTOR = 'vectorTile',
  PARCEL = 'parcel',
  PLACES = 'googlePlaces',
  TAX_PARCEL = 'taxParcel',
  WMTS = 'wmtsTile',
}

/** A value that acts as a disqualifier. */
export type BannedValue = string | number | boolean | null;

/**
 * LegendGroupConfig represents a group of legend symbols, as defined in Firestore, that can be toggled on or off
 * either as a group or individually.
 * Only the visibility state of the group and its symbols is exported for user state persistence, not the entire object.
 */
export interface LegendGroupConfig {
  /**
   * The label for the legend group.
   *
   * VectorTile Layers: Must match the first part of the layer name of the Esri Stylesheet.
   */
  label: string;
  /**
   * Indicates if the group is visible. This value will generally be calculated from the visibility of the symbols
   * within the group.
   */
  visible: boolean; // local state
  /**
   * An array of legend symbols representing the group's contents.
   *
   * Supported VectorTile styles:
   * - simple lines with a stroke color. Fill color will be used for the legend symbol but ignored when styling lines.
   * - simple polygons with a fill color and stroke color. Cross-hatched polygons are not supported.
   * - simple points with a fill color and stroke color.
   *
   * Icons and other complex symbols are not supported.
   */
  legendSymbols: {
    /** The fill color of the legend symbol */
    fillColor: string;
    /** The stroke color of the legend symbol */
    strokeColor: string;
    /**
     * The label for the legend symbol.
     *
     * VectorTile Layers: Must match the second part of the layer name of the Esri Stylesheet.
     */
    label: string;
    /** Indicates if the symbol is visible */
    visible: boolean; // local state
  }[];
}

/**
 * Represents the configuration for a layer option. Every layer must define at least one option and one sub-layer, but
 * can define multiple of each. The option is the top-level selection the user will select via radio button or dropdown,
 * and the sub-layer represents different things depending on the layer type.
 *
 * VectorTile Layers:
 * - A sub-layer represents an individual layer from the Esri VectorTileService. Often this will represent data to be
 * displayed at different zoom levels, but multiple sub-layers can be used to display different data at the same zoom.
 * - All sub-layers are expected to come from the same VectorTileService, so the URL map is defined at the layer level.
 */
export interface LayerOption {
  /** Label displayed to the user on the radio button options, if there is more than one option. */
  label: string;
  /** Whether this option is selected by default. If there is only one option, this must be `true`. */
  checked: boolean; // local state and default
  /**
   * The direction of the legend display, or `hidden` to give the layer no body at all. Currently
   * schemaVersion 2 only supports `vertical`.
   */
  legendStyle: 'horizontal' | 'vertical' | 'hidden';
  /**
   * When true, sub-layers can be individually toggled on/off via checkboxes in the legend UI.
   * The legend is also deduplicated and legend item filtering is disabled.
   * Used for layers like market/submarket where sub-layers share the same legend symbols.
   */
  subLayerToggleEnabled?: boolean;
  /**
   * When true, legend group labels are always shown even when there is only one group or one item.
   * By default, labels are hidden when redundant (single group or single item).
   */
  alwaysShowLegendLabel?: boolean;
  /**
   * When true, the user can fade this layer's fills with a slider so they can see the map underneath.
   * Only meaningful on vector tile layers that paint fills.
   */
  fillOpacityEnabled?: boolean;
  /**
   * If the layer does not use the default field types returned by the layer click handler, this field can be used to
   * specify a custom field mapping. This must point to a function which returns DisplayFields defined for the specific
   * layer type.
   */
  customFieldMapping?: string;
  /** Fields that are disqualified. */
  banFields?: string[];
  /** Values that should disqualify a field. */
  banValues?: BannedValue[];

  /** Sub layers, will typically display different sub-layers based on zoom level */
  subLayers: {
    /** The ID of the sub-layer */
    id: string;
    /** User-facing label for the sub-layer. Falls back to the ID if not provided. */
    label?: string;
    /** Whether this sub-layer is checked/visible. Defaults to true. */
    checked?: boolean;
    /**
     * VectorTile Layers: If there is more than one layer on the VectorTileService, use this property to select which
     * layers are clickable when this sub-layer is active.
     */
    clickableLayers: string[];
    /**
     * VectorTile Layers: If there is more than one layer on the VectorTileService, use this property to select which
     * layers are visible when this sub-layer is active.
     */
    visibleLayers: string[];
    /**
     * When true, the legend can be used to turn on or off the visibility of the legend symbols.
     */
    legendToggleEnabled: boolean;
    /**
     * The grouped legend symbols to be displayed when this sub-layer is active. The groups will be displayed
     * automatically based on the visibility and zoom settings of the sub-layer.
     */
    legendGroups: LegendGroupConfig[];
    /**
     * The minimum zoom level at which this sub-layer is visible.
     */
    minZoom: number;
    /**
     * The maximum zoom level at which this sub-layer is visible.
     */
    maxZoom: number;

    /**
     * The name of the feature property to use as the title for the InfoWindow. If this is not provided, or the field
     * is not present in the feature properties, the title will default to the layer name.
     *
     * If multiple fields are provided, all values found will be concatenated with parentheses.
     * @example 'FirstValue (SecondValue) (ThirdValue)'
     */
    titleFields?: string[];
    /**
     * @deprecated Do not use. Included for backwards compatibility with schemaVersion 1. Use a single entry in
     * `idFields` instead.
     */
    idField: string;
    /**
     * The name of the feature property to use as the unique identifier for the feature. The key should be the name of
     * a layer and the value should be the name of the field to use as the unique identifier for that layer.
     */
    idFields: Record<string, string>;
    /**
     * @deprecated Do not use. Included for backwards compatibility with schemaVersion 1. Use a single entry in
     * `idFields` instead.
     */
    featureIdKey: string;
    /**
     * @deprecated Do not use. Included for backwards compatibility with schemaVersion 1. Use a single entry in
     * `filterKeys` instead.
     */
    filterKey: string;
    /**
     * VectorTileGraphics: The name of the feature property to use as the filter for the layer. The key should be
     * the name of a layer and the value should be the names of the fields to use as the filter for that layer.
     */
    filterKeys: Record<string, string[]>;
    /**
     * VectorTileGraphics: The attribute filters this sub-layer offers, in the order they are shown.
     * Each filter's `field` names a tile property, which is the published layer's field *alias* rather than our own
     * contract name for it. See `LayerFilter`.
     */
    filters?: LayerFilter[];
    /**
     * The user's current value for each of `filters`, keyed by field. This is user state rather than configuration.
     */
    filterValues?: Record<string, LayerFilterValue>;
    /**
     * For matching legend group filters to specific layer features. Useful for when the same values appear in multiple
     * legend groups, but we only want to filter a single group.
     *
     * VectorTileGraphics: The name of the feature property to use as the filter group for the layer. The key should be
     * the name of a layer and the value should be the name of the field to use as the filter group for that layer.
     */
    filterGroupKey: string;
    /**
     * @deprecated
     * Used in the VectorTileGraphic click handler to determine which URL in the URL map to use for the feature server.
     * This has been replaced with layer lookups.
     */
    groupKey: string;
    /**
     * for layers with underlying data in BigQuery, this is the name of the table configuration to use for the
     * InfoWindow. It is a lookup key to retrieve the full configuration.
     */
    tableConfig: string;
  }[];
}

interface LayerConfig {
  /**
   * LOCAL ID of the layer. This is NOT the database ID and care must be taken to ensure this is unique to prevent data
   * corruption when saving user's state.
   */
  id: string;
  /**
   * The user-friendly name of the layer. This will be displayed in the layer selection menu.
   */
  name: string;
  /**
   * Whether the layer is a public layer. Public layers do not require a token to access. These may be self-hosted or
   * public services.
   */
  isPublicLayer: boolean;
  /**
   * The URL to the image representing the layer. This will be displayed in the layer selection menu.
   */
  imageUrl: string;
  /**
   * The minimum zoom level at which the layer is visible.
   */
  minZoom: number;
  /**
   * The maximum zoom level at which the layer is visible.
   */
  maxZoom: number;
  /**
   * The tooltip to display when hovering over the layer image in the layer selection menu.
   */
  tooltip: string;
  /**
   * The index of the layer in the layer selection menu. Lower numbers will appear higher in the list.
   */
  index: number;
  /**
   * A description of the layer's purpose or content.
   */
  description: string;
  /**
   * The type of layer (e.g., raster, vector). This must map to a supported graphic type and is used for selecting the
   * constructor or constructor helper.
   */
  type: string;
  /**
   * The options for the layer. Each option represents a different way the layer can be displayed or interacted with.
   */
  options: LayerOption[];
  /**
   * The name of the token to use for this layer. This should be an option available via the retrieveMapsToken cloud
   * function
   */
  tokenName: SupportedToken;
  /**
   * A custom handler function for click events on the layer.
   */
  customClickHandler?: string;
  /**
   * Whether to show a loading cursor while fetching feature data on click.
   */
  showLoadingCursor?: boolean;
  /**
   * Top-level url map for the layer. This map will have different entries depending on the layer type.
   * - `styleSheet`: URL to the style sheet for an esri vector tile layer
   * - `tileServer`: URL to the tile server for an esri vector tile layer
   * - `wmtsTileUrl`: URL to the WMTS tile server
   * - `featureServer`: URL to the feature server for an esri vector tile layer
   */
  urlMap: Record<string, string>;
  /**
   * The schema version of the layer. This is used to determine how to parse the layer configuration. This should be
   * incremented whenever the layer configuration changes in a way that is not backwards compatible.
   */
  schemaVersion: 2 | null;

  // Metrics
  usageMetricName: string;
  rate: number; // TODO: decide if this should all go under "metering" or something
  totalUsageThreshold: number;
  showUsageThreshold: number;

  // App/User State
  map: google.maps.Map;
  checked: boolean;
}

/**
 * Represents the state of a layer. This is used to save and restore the state of the layer when the user navigates away
 * from the map and returns.
 */
interface LayerState {
  id: string;
  checked: boolean;
  options: {
    id: string;
    checked: boolean;
    subLayers: {
      id: string;
      checked?: boolean;
      filterValues?: Record<string, LayerFilterValue>;
      legendGroups: {
        label: string;
        visible: boolean;
        legendSymbols: {
          label: string;
          visible: boolean;
        }[];
      }[];
    }[];
  }[];
  /** Absent means the user never moved the slider, or reset it. */
  fillOpacity?: number;
  schemaVersion: number;
}

export class MapLayer {
  isPublicLayer: boolean;
  id: string;
  name: string;
  imageUrl: string;
  minZoom: number;
  maxZoom: number;
  tooltip: string;
  index: number;
  description: string;
  type: LayerType;
  options: LayerOption[];
  usageMetricName: string;
  rate: number;
  totalUsageThreshold: number;
  showUsageThreshold: number;
  tokenName: SupportedToken;
  customClickHandler?: string;
  showLoadingCursor: boolean;
  urlMap: Record<string, string>;
  /**
   * A reference to the ESRI style sheet for this layer. Currently, it is expected this stylesheet conforms to the
   * Mapbox style spec.
   */
  styleSheet: StyleSpecification;
  /**
   * Tracks all of the legend groups for the layer, including those that are not currently visible. The items are
   * built from an ESRI stylesheet.
   */
  legendGroups: LegendGroup[];
  legendStyle: 'vertical' | 'horizontal' | 'hidden';
  subLayerToggleEnabled: boolean;
  alwaysShowLegendLabel: boolean;
  /**
   * Multiplier applied to the fill alpha the stylesheet asks for. Undefined leaves the stylesheet alone. Held at the
   * layer level even though `fillOpacityEnabled` is per option, so one slider covers the whole layer.
   */
  fillOpacity?: number;

  checked = false;
  loading = false;

  schemaVersion = 2;

  initialized: Promise<void>;

  // private members
  private map: google.maps.Map;
  private resolveInitialized: () => void;

  private graphic: Graphic | WMTSTileGraphic | VectorTileGraphic | REPGraphic;
  private _initialized = false;

  constructor(layerOptions: LayerConfig) {
    if (!(layerOptions.schemaVersion === 2)) {
      throw new Error(`Invalid schema version: ${layerOptions.schemaVersion}`);
    }
    this.isPublicLayer = layerOptions.isPublicLayer;
    this.id = layerOptions.id;
    this.name = layerOptions.name;
    this.imageUrl = layerOptions.imageUrl;
    this.minZoom = layerOptions.minZoom;
    this.maxZoom = layerOptions.maxZoom;
    this.tooltip = layerOptions.tooltip;
    this.index = layerOptions.index;
    this.description = layerOptions.description;
    // make sure the type is valid
    if (!Object.values(LayerType).includes(layerOptions.type as LayerType)) {
      throw new Error(`Invalid type: ${layerOptions.type}`);
    }
    this.type = layerOptions.type as LayerType;
    this.options = layerOptions.options;
    this.options.forEach((option) => {
      if (!option.subLayers) {
        option.subLayers = [];
      }
      option.subLayers.forEach((subLayer) => {
        if (subLayer.checked === undefined) {
          subLayer.checked = true;
        }
        if (!subLayer.legendGroups) {
          subLayer.legendGroups = [];
        }
        subLayer.legendGroups.forEach((group) => {
          if (!group.legendSymbols) {
            group.legendSymbols = [];
          }
          group.visible = group.legendSymbols.every((legendSymbol) => legendSymbol.visible) ?? true;
        });
      });
    });
    const selectedOption = this.selectedOption;
    this.legendStyle = selectedOption?.legendStyle;
    this.subLayerToggleEnabled = selectedOption?.subLayerToggleEnabled ?? false;
    this.alwaysShowLegendLabel = selectedOption?.alwaysShowLegendLabel ?? false;
    this.rate = layerOptions.rate;
    this.totalUsageThreshold = layerOptions.totalUsageThreshold;
    this.showUsageThreshold = layerOptions.showUsageThreshold;
    this.tokenName = layerOptions.tokenName;
    this.usageMetricName = layerOptions.usageMetricName;
    this.customClickHandler = layerOptions.customClickHandler;
    this.showLoadingCursor = layerOptions.showLoadingCursor ?? false;
    this.urlMap = layerOptions.urlMap;

    this.map = layerOptions.map;
    this.initialized = new Promise((resolve) => {
      this.resolveInitialized = resolve;
    });
  }

  async initialize(): Promise<void> {
    if (this._initialized || this.loading) {
      return this.initialized;
    }
    this.loading = true;
    // TODO: basically everything in this section needs to be moved to the respective graphic classes and make them
    // responsible for their own initialization given the layer config. Probably the best thing to add would be emitted
    // events for opening infoWindows.
    if (this.type === LayerType.DRONE) { // Drone
      this.graphic = await Graphics.create('drone', this.id, {featureType: 'drone'}, true);
    } else if (this.type === LayerType.PLACES) { // Google Places
      this.graphic = await Graphics.create('places', this.id, {featureType: 'places'}, true);
    } else if (this.type === LayerType.PARCEL) {
      this.graphic = await Graphics.create('parcels', this.id, {featureType: 'rep'}, this.zoomOk());
      // @ts-ignore
      this.graphic.subscribe('parcel_click', (event) => {
        const contents = event.payload.map((parcel) => ({
          // parcels are not formatted as info window content in the graphic because
          // we listen for the same event when adding multiple parcels, which doesn't open info windows.
          content: createParcelContent(Object.assign(parcel, {graphic: this.graphic})),
        }));
        getStore().commit('infowindow/updateInfoWindowContents', contents);
      });
    } else if (this.type === LayerType.TAX_PARCEL) {
      this.graphic = new TaxParcelGraphic();
      Graphics.register('taxParcels', this.id, this.graphic);
      const graphic = this.graphic as TaxParcelGraphic;
      this.legendGroups = graphic.createLegendGroups(this.options.flatMap((option) => option.subLayers));
    } else if (this.type === LayerType.WMTS) { // WMTS
      if (!this.urlMap) {
        throw new Error(`No URL map found for WMTS Tile layer: ${this.id}. Sub-layer URL map required`);
      }
      this.graphic = await createWMTSTileGraphic(this);
      Graphics.register('wmtsTile', this.id, this.graphic);
      this.graphic.subscribe('open_info_window', (event) => {
        getStore().commit('infowindow/updateInfoWindowContents', {content: event.payload});
      });
    } else if (this.type === LayerType.ESRI_VECTOR) { // ESRI Vector
      if (!this.urlMap) {
        throw new Error(`No URL map found for ESRI Vector layer: ${this.id}. Sub-layer URL map required`);
      }

      try {
        // eslint-disable-next-line no-undef
        const headers: HeadersInit = {};
        // It is currently assumed if the style sheet does not require a token if it is:
        // - hosted in Cloud Storage
        // - is using custom style url
        if (!this.isPublicLayer &&
          !this.urlMap['styleSheet'].includes('storage.googleapis.com') &&
          !this.urlMap['styleSheet'].includes('content/items/')
        ) {
          headers['Authorization'] = `Bearer ${await TokenService.getToken('arcGisServerToken')}`;
        }

        this.styleSheet = await getJsonByURL(this.urlMap['styleSheet'], headers) as StyleSpecification;

        this.legendGroups = await createLegendGroups(this);
      } catch (error) {
        if (error instanceof Error) {
          error.message = `Could not build legend items for layer "${this.id}": ${error.message}`;
        }
        console.warn(error);
      }

      this.graphic = new VectorTileGraphic(this);
      Graphics.register('vectorTile', this.id, this.graphic);
      this.graphic.subscribe('open_info_window', (event) => {
        const contents = event.payload.map((subject) => ({content: subject.content}));
        getStore().commit('infowindow/updateInfoWindowContents', contents);
      });

      MapService.addMapListener(`zoom_changed-${this.id}`, 'zoom_changed', () => {
        const subLayers = this.activeSubLayers();
        const graphic = this.graphic as VectorTileGraphic;
        graphic.setVisibleLayers(
          subLayers.reduce((acc, subLayer) => acc.concat(subLayer.visibleLayers), []).toReversed(),
        );
        graphic.updateLegendFiltering(this.activeLegendGroups);
      });
    } else {
      throw new Error(`Invalid type for schema version 2: ${this.type}`);
    }
    this.loading = false;
    this._initialized = true;
    this.resolveInitialized();
  }

  zoomOk(): boolean {
    if (!this.map) {
      console.warn(`MapLayer.zoomOk() called before map is set`);
      return false;
    }
    const zoom = this.map.getZoom();
    return zoom >= this.minZoom && zoom <= this.maxZoom;
  }

  /**
   * Returns sub-layers valid at the current zoom for the selected option, with fallback to other options.
   * @param filterByChecked When true, excludes sub-layers the user has unchecked (only applies when
   *   `subLayerToggleEnabled` is true).
   */
  private getSubLayers(filterByChecked: boolean): LayerOption['subLayers'] {
    if (!this.options?.length || !this.map) return [];

    const zoom = this.map.getZoom();
    const getValidSubLayers = (option: LayerOption) => {
      return option.subLayers.filter((subLayer) => {
        const zoomValid = zoom >= subLayer.minZoom && zoom <= subLayer.maxZoom;
        if (!zoomValid) return false;
        if (filterByChecked && this.subLayerToggleEnabled) return subLayer.checked !== false;
        return true;
      });
    };

    const selectedOption = this.selectedOption;
    const active = selectedOption ? getValidSubLayers(selectedOption) : [];

    if (active.length > 0) return active;

    // When sub-layer toggling is enabled, don't fall back to other options if the user
    // explicitly unchecked all sub-layers at this zoom level.
    if (filterByChecked && this.subLayerToggleEnabled) {
      const hasSubLayersAtZoom = selectedOption?.subLayers.some(
        (sl) => zoom >= sl.minZoom && zoom <= sl.maxZoom,
      );
      if (hasSubLayersAtZoom) return [];
    }

    // Fallback: If the selected option renders nothing at this zoom level,
    // find any option that does. This allows for "zoom-dependent" options
    // that merge into a single view at other zoom levels.
    for (const option of this.options) {
      if (option === selectedOption) continue;
      const fallback = getValidSubLayers(option);
      if (fallback.length > 0) return fallback;
    }

    return [];
  }

  activeSubLayers(): LayerOption['subLayers'] {
    return this.getSubLayers(true);
  }

  /**
   * Returns all sub-layers valid at current zoom for the selected option, regardless of checked state.
   * Used for UI display (e.g. sub-layer checkboxes).
   */
  get displaySubLayers(): LayerOption['subLayers'] {
    return this.getSubLayers(false);
  }

  toggleSubLayer(subLayerId: string): void {
    const option = this.selectedOption;
    if (!option) return;

    const subLayer = option.subLayers.find((sl) => sl.id === subLayerId);
    if (!subLayer) return;

    subLayer.checked = subLayer.checked === false;

    if (this.type !== LayerType.ESRI_VECTOR) return;

    const visibleSubLayers = this.activeSubLayers();
    const graphic = this.graphic as VectorTileGraphic;
    graphic.setVisibleLayers(
      visibleSubLayers.reduce((acc, sl) => acc.concat(sl.visibleLayers), [] as string[]).toReversed(),
    );
    graphic.filterKeys = visibleSubLayers.reduce((acc, sl) => ({...acc, ...sl.filterKeys}), {});
    graphic.updateActiveFilters(this.activeFilters);

    if (visibleSubLayers.some((sl) => sl.clickableLayers.length)) {
      graphic.setClickable(true);
    } else {
      graphic.setClickable(false);
    }
    this.updateLegendFiltering(true);
  }

  setMap(map: google.maps.Map | null): void {
    if (map) {
      // set and hold a reference to the map, it will be needed for zoom calculations even if the layer is not visible
      this.map = map;
      // Async graphics reject after this method returns; without a catch that surfaces as an
      // unattributed unhandled rejection and the layer silently fails to load (BMAPS-3652).
      Promise.resolve(this.graphic.setMap(this.map)).catch((error) => {
        console.error(wrapError(error, `Failed to load layer "${this.id}"`));
      });
      const subLayers = this.activeSubLayers();
      if (subLayers.some((subLayer) => subLayer.clickableLayers.length)) {
        this.graphic.setClickable(true);
      }
      if (this.type === LayerType.ESRI_VECTOR) {
        (this.graphic as VectorTileGraphic).updateLegendFiltering(this.activeLegendGroups);
      }
      if (this.type === LayerType.TAX_PARCEL) {
        (this.graphic as TaxParcelGraphic).updateLegendFiltering(this.activeLegendGroups);
      }
    } else {
      // A checked layer that never initialized (e.g. enabled while out of zoom range) has no graphic to unload.
      if (!this.graphic) return;
      // if the graphic service has the layer registered, set render to false
      // this occurs specifically for parcel layers.
      if (Graphics.exists(this.id, this.id)) {
        Graphics.setRender(this.id, false);
      }
      Promise.resolve(this.graphic.setMap(null)).catch((error) => {
        console.error(wrapError(error, `Failed to unload layer "${this.id}"`));
      });
    }
  }

  setOption(optionLabel: string): void {
    const previousOption = this.selectedOption;
    this.options.forEach((option) => {
      option.checked = option.label === optionLabel;
      if (option.checked) {
        this.legendStyle = option.legendStyle;
        this.alwaysShowLegendLabel = option.alwaysShowLegendLabel ?? false;
        // Transfer sub-layer checked state from old option to new option by label
        if (this.subLayerToggleEnabled && previousOption && previousOption !== option) {
          option.subLayers.forEach((subLayer) => {
            const match = previousOption.subLayers.find((sl) => sl.label === subLayer.label);
            if (match) {
              subLayer.checked = match.checked;
            }
          });
        }
      }
    });
    const subLayers = this.activeSubLayers();
    if (!subLayers?.length) return;

    const graphic = this.graphic as VectorTileGraphic;
    graphic.setVisibleLayers(
      subLayers.reduce((acc, subLayer) => acc.concat(subLayer.visibleLayers), []).toReversed(),
    );
    this.applyFillOpacity();
    graphic.filterGroupKey = subLayers[0].filterGroupKey;
    graphic.filterKey = subLayers[0].filterKey;
    graphic.filterKeys = subLayers.reduce((acc, subLayer) => ({...acc, ...subLayer.filterKeys}), {});
    graphic.updateActiveFilters(this.activeFilters);
    if (subLayers.some((subLayer) => subLayer.clickableLayers.length)) {
      graphic.setClickable(true);
    } else {
      graphic.setClickable(false);
    }
    this.updateLegendFiltering(true);
  }

  /** Read off the active option so switching options cannot leave a stale value behind. */
  get fillOpacityEnabled(): boolean {
    return this.selectedOption?.fillOpacityEnabled ?? false;
  }

  /**
   * @param opacity a multiplier between 0 and 1, or undefined to go back to the stylesheet's own alpha
   */
  setFillOpacity(opacity: number | undefined): void {
    this.fillOpacity = opacity;
    this.applyFillOpacity();
  }

  /** An option that does not offer the slider is drawn as its stylesheet asks, whatever the layer's value is. */
  private applyFillOpacity(): void {
    if (this.type !== LayerType.ESRI_VECTOR || !this.graphic) return;
    (this.graphic as VectorTileGraphic).setFillOpacity(this.fillOpacityEnabled ? this.fillOpacity : undefined);
  }

  updateLegendFiltering(forceUpdate = false): void {
    if (this.type === LayerType.ESRI_VECTOR) {
      (this.graphic as VectorTileGraphic).updateLegendFiltering(this.activeLegendGroups, forceUpdate);
    }
    if (this.type === LayerType.TAX_PARCEL) {
      (this.graphic as TaxParcelGraphic).updateLegendFiltering(this.activeLegendGroups);
    }
  }

  get activeLegendGroups(): LegendGroup[] {
    if (!this.legendGroups) return [];
    return this.activeSubLayers()
      .map((subLayer) =>
        subLayer.legendGroups
          .map((group) => this.legendGroups.find((g) => g.label === group.label))
          .filter(Boolean),
      )
      .flat();
  }

  /** The option the user has chosen. */
  get selectedOption(): LayerOption | undefined {
    return this.options?.find((option) => option.checked);
  }

  /**
   * The sub-layers the menu reads and the render path applies. Falls back to the first option, so
   * the menu still has something to show before anything is chosen.
   */
  get configuredSubLayers(): LayerOption['subLayers'] {
    return (this.selectedOption ?? this.options?.[0])?.subLayers ?? [];
  }

  /** Whether this layer offers any attribute filters at all. */
  get hasFilters(): boolean {
    return this.configuredSubLayers.some((subLayer) => subLayer.filters?.length);
  }

  /** Whether the user has narrowed this layer, for the menu's activity dot. */
  get hasActiveFilters(): boolean {
    return this.configuredSubLayers.some((subLayer) =>
      subLayer.filters?.some((filter) => toActiveFilter(filter, subLayer.filterValues?.[filter.field] ?? {}) !== null),
    );
  }

  /**
   * Whether the layer asks for a legend. Read off the config rather than `legendGroups`, which every
   * vector tile layer gets from its stylesheet whether or not the layer wants one shown.
   */
  get hasLegend(): boolean {
    return this.configuredSubLayers.some((subLayer) => subLayer.legendGroups?.length > 0);
  }

  /**
   * The active attribute filters, keyed by the MVT layer name their features carry.
   *
   * Keyed by layer name rather than sub-layer id because the render path sees one flat list of
   * visible layer names and cannot trace a feature back to the sub-layer that declared the filter.
   */
  get activeFilters(): ActiveFiltersByLayer {
    const activeFilters: ActiveFiltersByLayer = {};
    for (const subLayer of this.configuredSubLayers) {
      if (!subLayer.filters?.length) continue;
      const active = subLayer.filters
        .map((filter) => toActiveFilter(filter, subLayer.filterValues?.[filter.field] ?? {}))
        .filter((filter): filter is ActiveLayerFilter => filter !== null);
      if (!active.length) continue;
      for (const layerName of subLayer.visibleLayers) {
        activeFilters[layerName] = (activeFilters[layerName] ?? []).concat(active);
      }
    }
    return activeFilters;
  }

  /**
   * Replaces one sub-layer's filter values, so the caller owns the whole set for that sub-layer —
   * clearing a single filter means passing the others back without it.
   * @param subLayerId The sub-layer whose filters these are.
   * @param values The user's value for each filter, keyed by `LayerFilter.field`.
   */
  setSubLayerFilterValues(subLayerId: string, values: Record<string, LayerFilterValue>): void {
    const subLayer = this.configuredSubLayers.find((sl) => sl.id === subLayerId);
    if (!subLayer) return;
    subLayer.filterValues = values;
    if (this.type !== LayerType.ESRI_VECTOR || !this.graphic) return;
    (this.graphic as VectorTileGraphic).updateActiveFilters(this.activeFilters);
  }

  get state(): LayerState {
    const state = {
      id: this.id,
      checked: this.checked,
      options: this.options.map((option) => {
        return {
          id: option.label,
          checked: option.checked,
          subLayers: option?.subLayers.map((subLayer) => {
            const legendGroups = subLayer.legendGroups?.map((group) => {
              const legendGroup = this.legendGroups?.find((g) => g.label === group.label);
              return {
                label: group.label,
                visible: legendGroup?.isVisible() ?? true,
                legendSymbols: legendGroup?.legendSymbols?.map((legendSymbol: LegendItem) => ({
                  label: legendSymbol.label,
                  visible: legendSymbol.visible ?? true,
                })),
              };
            }) ?? [];
            return {
              id: subLayer.id,
              checked: subLayer.checked ?? true,
              legendGroups,
              ...(subLayer.filters?.length ? {filterValues: subLayer.filterValues ?? {}} : {}),
            };
          }),
        };
      }),
      // Firestore rejects undefined, so the key is left off rather than written with no value.
      ...(this.fillOpacity === undefined ? {} : {fillOpacity: this.fillOpacity}),
      schemaVersion: this.schemaVersion,
    };
    return state;
  }

  set state(state: LayerState) {
    if (!state) return;
    this.checked = state.checked ?? this.checked;
    this.fillOpacity = state.fillOpacity ?? this.fillOpacity;
    this.options = this.options.map((option) => {
      const stateOption = state.options?.find((opt) => opt.id === option.label);
      if (!stateOption) return option;
      option.checked = stateOption.checked ?? option.checked;
      option.subLayers = option.subLayers.map((subLayer) => {
        const stateSubLayer = stateOption.subLayers.find((sl) => sl.id === subLayer.id);
        if (!stateSubLayer) return subLayer;
        subLayer.checked = stateSubLayer.checked ?? subLayer.checked ?? true;
        if (subLayer.filters?.length) {
          const saved = stateSubLayer.filterValues ?? {};
          subLayer.filterValues = Object.fromEntries(
            subLayer.filters
              .filter((filter) => saved[filter.field])
              .map((filter) => [filter.field, saved[filter.field]]),
          );
        }
        subLayer.legendGroups = subLayer.legendGroups?.map((group) => {
          const stateGroup = stateSubLayer?.legendGroups?.find((g) => g.label === group.label);
          if (!stateGroup) return group;
          group.visible = stateGroup.visible ?? group.visible ?? true;
          // Some layers expect the legend symbols to be defined in the layer config. So if the legend symbols are not
          // defined in the state, we will just return the existing symbols.
          if (stateGroup.legendSymbols?.length) {
            group.legendSymbols = stateGroup.legendSymbols?.map((stateLegendSymbol) => {
              const existingSymbol = group.legendSymbols?.find((ls) => ls.label === stateLegendSymbol.label);
              if (existingSymbol) {
                existingSymbol.visible = stateLegendSymbol.visible ?? existingSymbol.visible ?? true;
                return existingSymbol;
              } else {
                return {
                  label: stateLegendSymbol.label,
                  visible: stateLegendSymbol.visible ?? true,
                  fillColor: '',
                  strokeColor: '',
                };
              }
            });
          }
          return group;
        });
        return subLayer;
      });
      return option;
    });
  }

  get validOptions(): LayerOption[] {
    if (!this.map) return this.options;
    const zoom = this.map.getZoom();
    if (zoom === undefined) return this.options;

    return this.options.filter((option) => {
      // An option is valid if it has at least one subLayer that is valid for the current zoom
      return option.subLayers.some((subLayer) => {
        return zoom >= subLayer.minZoom && zoom < subLayer.maxZoom;
      });
    });
  }
}
